import React, { ComponentType, useContext } from 'react';
import { ObjectFieldTemplateProps } from '@rjsf/utils';
import { getDefaultRegistry } from '@rjsf/core';
import { RowLayoutContext, rowCellClass } from './rowLayout';
import { ItemNumberingContext } from './itemNumbering';

// Looked up on first render, not at import: suites that mock @rjsf/core
// still import SchemaForm, which registers this template.
let defaultTemplate: ComponentType<ObjectFieldTemplateProps> | undefined;
const getDefaultTemplate = () =>
  (defaultTemplate ??= getDefaultRegistry().templates.ObjectFieldTemplate);

// Pydantic titles a nested model after its class ("CmoreFieldMapping") unless
// the connector sets one, so a one-word CamelCase title is split into words:
// "Cmore Field Mapping", "ER Destination". Titles with spaces are left as-is.
export const splitClassName = (title: string) =>
  /^[A-Z][A-Za-z0-9]*$/.test(title)
    ? title
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    : title;

// rjsf's own object template everywhere except the items of a row-layout
// array (see rowLayout.ts), which render their properties as one row of cells
// with no legend: the list's caption line names the columns.
const RowAwareObjectFieldTemplate = (props: ObjectFieldTemplateProps) => {
  const role = useContext(RowLayoutContext);
  const numbering = useContext(ItemNumberingContext);
  // See itemNumbering.ts: only the item object itself takes the number.
  const below = { number: numbering.number, isItem: false };
  if (role !== 'item') {
    const DefaultObjectFieldTemplate = getDefaultTemplate();
    const readable = props.title && splitClassName(props.title);
    const title =
      numbering.isItem && numbering.number && readable
        ? `${numbering.number} ${readable}`
        : readable;
    return (
      <ItemNumberingContext.Provider value={below}>
        <DefaultObjectFieldTemplate {...props} title={title} />
      </ItemNumberingContext.Provider>
    );
  }
  const visible = props.properties.filter((property) => !property.hidden);
  return (
    <ItemNumberingContext.Provider value={below}>
      <div
        className="flex w-full flex-col gap-2 sm:flex-row sm:items-start"
        data-testid="RowObjectFieldTemplate-row"
      >
        {props.properties.map((property) =>
          property.hidden ? (
            <RowLayoutContext.Provider key={property.name} value={null}>
              {property.content}
            </RowLayoutContext.Provider>
          ) : (
            <div
              key={property.name}
              className={rowCellClass(
                visible.indexOf(property),
                visible.length,
              )}
              data-testid="RowObjectFieldTemplate-cell"
            >
              <RowLayoutContext.Provider value="cell">
                {property.content}
              </RowLayoutContext.Provider>
            </div>
          ),
        )}
      </div>
    </ItemNumberingContext.Provider>
  );
};

export default RowAwareObjectFieldTemplate;
